/* eslint-disable max-lines */
/**
 * The chunk-recording capture graph, seen from the side that drives it: device
 * enumeration, input switching, the Web Audio source node and the volume meter.
 *
 * These seven functions were the seven effects of a Rematch slice called
 * `chunkAudio`. The slice held `state: {}` and one `clear` reducer nothing
 * dispatched, so there is no state here and no atom to hold it — every handle
 * these functions read and write (`mediaStreamAudioSourceNode`, `analyser`, the
 * two interval ids, the device list, the selected device) belongs to the `audio`
 * slice, and stays there until that slice finds a non-store home. Cross-slice
 * writes therefore still go through the store singleton, which is why this
 * module imports it and is carried on the containment baseline rather than off
 * it. That is the honest shape of this seam today: it is orchestration over
 * another slice's state, not state of its own.
 *
 * The bodies are the effect bodies unchanged. Two mechanical differences, both
 * of them plumbing: a cross-slice `dispatch.<slice>.<method>` is now
 * `store.dispatch.<slice>.<method>`, and a self-dispatch is now a direct call —
 * `init` from four sites and `stop` from one. Nothing about the media,
 * permission or device logic moved, and the disabled alternatives are carried
 * verbatim as comments rather than tidied, so a reviewer diffing this file
 * against the model finds only the two rewrites above.
 *
 * `src/lib/audio/` reads as the obvious home and is not available: the capture
 * kit has already moved that engine into `@heidi/capture` and left three
 * generated door shims behind, and its migration ratchets admit nothing else
 * into the tree — a new file there is one violation and an importer reaching
 * into it is a second. `packages/capture` is the remedy those ratchets name,
 * and it is closed to this module for a reason that is not a technicality:
 * every write below lands in the `audio` Rematch slice through the app store
 * singleton, and a package's data layer may not import that. So the file sits
 * here, one level up, app-side and outside the frozen tree, until the `audio`
 * slice itself finds a non-store home and both halves can go into the kit
 * together.
 *
 * Nothing runs at module scope: the imports and one type alias are the whole of
 * this module's evaluation, and every store access sits inside a function body.
 * That is what keeps the new `audioModel` → here → `@/store` import edge from
 * becoming an initialization-order dependency, which on this particular path is
 * not hypothetical — three static-cycle breaks already exist in this tree, each
 * one a "cannot access before initialization" that only appeared in a
 * production build.
 *
 * Callers today are the five chunk-recording arms in `src/models/audioModel.ts`.
 * Three of them are behind an `isTauri()` early return in the calling effect
 * (`startVolumeDetection`, `getMicPermission`, `initializeAudioDevices`),
 * because the desktop app captures natively and never reaches this browser-media
 * path; the other two (`stopVolumeDetection`, `setSelectedAudioSource`) are not,
 * and that is how it was before this file existed. Nothing here reads a
 * permission API or branches on the platform, and nothing here should start to:
 * a WebView permission answer is not the authority for a capture the OS allows.
 */
import {
  lastUsedAudioInputAtom,
  setLastUsedAudioInput,
} from '@/atoms/persistedPreferencesAtoms';
import { sleep } from '@/lib/utils';
import { store } from '@/store';
import { logger } from '@/utils/logger/logger';
import { getDefaultStore } from 'jotai';
import { isEqual, max } from 'lodash-es';
import { isSafari } from 'react-device-detect';
import { MediaStreamAudioSourceNode } from 'standardized-audio-context';

export type VolumeDetectLevel = 'normal' | 'low' | 'noSound';

export async function init() {
  const { selectedAudioSource, mediaStreamAudioDestinationNode } = store.getState().audio;

  if (!store.getState().audio.mediaStreamAudioSourceNode) {
    const audioStream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: isSafari
        ? {
            deviceId: selectedAudioSource?.deviceId,
          }
        : {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
            deviceId: selectedAudioSource?.deviceId,
          },
    });

    const audioContext = store.getState().audio.mediaStreamAudioDestinationNode?.context;

    if (!audioContext) {
      return;
    }
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }
    const newMediaStreamAudioSourceNode = new MediaStreamAudioSourceNode(audioContext, {
      mediaStream: audioStream,
    });

    if (store.getState().audio.mediaStreamAudioSourceNode) {
      store.getState().audio.mediaStreamAudioSourceNode?.disconnect();
    }
    if (!mediaStreamAudioDestinationNode) {
      return;
    }

    newMediaStreamAudioSourceNode.connect(mediaStreamAudioDestinationNode);
    const analyser = store.getState().audio.analyser;
    if (analyser) {
      newMediaStreamAudioSourceNode.connect(analyser);
    }

    store.dispatch.audio.setMediaStreamAudioSourceNode(newMediaStreamAudioSourceNode);
  }
}

export async function refreshTracks() {
  const endedTracks = store.getState().audio.mediaStreamAudioDestinationNode?.stream
    ? store
        .getState()
        .audio.mediaStreamAudioDestinationNode?.stream.getTracks()
        .filter((x) => x.readyState === 'ended')
    : [];

  if (endedTracks && endedTracks.length > 0) {
    store.dispatch.audio.setMediaStreamAudioSourceNode(undefined);
    await init();

    logger.debug(
      store
        .getState()
        .audio.mediaStreamAudioDestinationNode?.stream.getTracks()
        .map((x) => x.readyState)
    );
  }
}

export function stop() {
  if (store.getState().audio.mediaStreamAudioSourceNode) {
    store.getState().audio.mediaStreamAudioSourceNode?.disconnect();
  }

  if (store.getState().audio.audioStream) {
    store
      .getState()
      .audio.audioStream?.getTracks()
      .forEach((track) => {
        track.stop();
      });
  }

  store.dispatch.audio.setMediaStreamAudioSourceNode(undefined);
  store.dispatch.audio.setAudioSource(undefined);
}

export async function startVolumeDetection() {
  try {
    const { isDetectingVolume } = store.getState().audio;

    if (!store.getState().audio.mediaStreamAudioSourceNode) {
      await init();
    }

    const audioContext = store.getState().audio.mediaStreamAudioDestinationNode?.context;
    if (!store.getState().audio.analyser) {
      if (!audioContext) {
        logger.warn('No audio context present');
        return;
      }
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      analyser.minDecibels = -127;
      analyser.maxDecibels = 0;
      analyser.smoothingTimeConstant = 0.4;
      const node = store.getState().audio.mediaStreamAudioSourceNode;
      if (!node) {
        logger.warn('No audio source node present');
        return;
      }
      node.connect(analyser);
      store.dispatch.audio.setAnalyser(analyser);
    }

    if (isDetectingVolume) {
      return;
    }

    store.dispatch.audio.isDetectingVolume(true);

    let maxVolume = 0;

    const analyser = store.getState().audio.analyser;
    if (!analyser) {
      return;
    }
    const volumes = new Uint8Array(analyser.frequencyBinCount);

    const volumeCallback = () => {
      try {
        analyser?.getByteFrequencyData(volumes);
        let volumeSum = 0;
        for (const volume of volumes) {
          volumeSum += volume;
        }
        const averageVolume = volumeSum / volumes.length;

        const meterVolume = (averageVolume * 100) / 127;
        store.dispatch.audio.setMeterVolume(meterVolume);

        maxVolume = max([maxVolume, meterVolume]) || 0;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        clearInterval(store.getState().audio.volumeInterval);
        clearInterval(store.getState().audio.volumeLevelInterval);
        if (!store.getState().audio.mediaStreamAudioSourceNode) {
          void init();
        } else if (!store.getState().audio.analyser) {
          void store.dispatch.audio.startVolumeDetection();
        }
      }
    };

    const volumeInterval = setInterval(volumeCallback, 200);
    store.dispatch.audio.setVolumeInterval(volumeInterval);

    let countDown = 0;
    const checkVolume2 = () => {
      const threshold1 = 10;
      const threshold2 = 20;

      countDown--;
      if (countDown > 0) {
        return;
      }

      if (maxVolume < threshold1) {
        store.dispatch.audio.setVolumeDetectLevel('noSound');
        countDown = 3;
      } else if (maxVolume < threshold2) {
        store.dispatch.audio.setVolumeDetectLevel('low');
        countDown = 3;
      } else {
        store.dispatch.audio.setVolumeDetectLevel('normal');
        countDown = 50;
      }

      maxVolume = 0;
    };

    const volumeLevelInterval = setInterval(checkVolume2, 1000);
    store.dispatch.audio.setVolumeLevelInterval(volumeLevelInterval);
  } catch (e) {
    logger.error(e);
  }
}

// async startVolumeDetection() {
//   try {
//     const { isDetectingVolume } = store.getState().audio;
//     logger.debug(
//       'startVolumeDetection chunk, was detecting volume: ',
//       isDetectingVolume
//     );
//     if (!store.getState().audio.mediaStreamAudioSourceNode) {
//       logger.debug('init audio stream');
//       await dispatch.chunkAudio.init();
//     }

//     if (!store.getState().audio.analyser) {
//       logger.debug('init audio context');
//       const audioContext = store.getState().audio.mediaStreamAudioDestinationNode?.context;
//       if (!audioContext) {
//         logger.debug('no audio context');
//         return;
//       }
//       const analyser = audioContext.createAnalyser();
//       analyser.fftSize = 512;
//       analyser.minDecibels = -127;
//       analyser.maxDecibels = 0;
//       analyser.smoothingTimeConstant = 0.4;

//       store.getState().audio.mediaStreamAudioSourceNode?.connect(analyser);
//       alert('setting analyser');
//       dispatch.audio.setAnalyser(analyser);
//     }

//     if (isDetectingVolume) {
//       return;
//     }

//     dispatch.audio.isDetectingVolume(true);

//     let maxVolume = 0;
//     const analyser = store.getState().audio.analyser;
//     if (!analyser) {
//       return;
//     }
//     const volumes = new Uint8Array(analyser.frequencyBinCount);
//     const volumeCallback = () => {
//       try {
//         analyser.getByteFrequencyData(volumes);
//         let volumeSum = 0;
//         for (const volume of volumes) {
//           volumeSum += volume;
//         }
//         const averageVolume = volumeSum / volumes.length;

//         const meterVolume = (averageVolume * 100) / 127;

//         dispatch.audio.setMeterVolume(meterVolume);

//         maxVolume = max([maxVolume, meterVolume]) || 0;
//       } catch (e) {
//         clearInterval(store.getState().audio.volumeInterval);
//         clearInterval(store.getState().audio.volumeLevelInterval);
//         if (!store.getState().audio.mediaStreamAudioSourceNode) {
//           void dispatch.chunkAudio.init();
//         } else if (!store.getState().audio.analyser) {
//           void dispatch.audio.startVolumeDetection();
//         }
//       }
//     };

//     const volumeInterval = setInterval(volumeCallback, 200);
//     dispatch.audio.setVolumeInterval(volumeInterval);

//     let countDown = 0;
//     const checkVolume2 = () => {
//       const threshold1 = 10;
//       const threshold2 = 20;
//       countDown--;

//       if (countDown > 0) {
//         return;
//       }

//       if (maxVolume < threshold1) {
//         dispatch.audio.setVolumeDetectLevel('noSound');
//         countDown = 3;
//       } else if (maxVolume < threshold2) {
//         dispatch.audio.setVolumeDetectLevel('low');
//         countDown = 3;
//       } else {
//         dispatch.audio.setVolumeDetectLevel('normal');
//         countDown = 50;
//       }

//       maxVolume = 0;
//     };

//     const volumeLevelInterval = setInterval(checkVolume2, 1000);
//     dispatch.audio.setVolumeLevelInterval(volumeLevelInterval);
//   } catch (e) {
//     logger.debug(e);
//   }
// },

export function stopVolumeDetection() {
  logger.debug('stop volume detection');
  store.dispatch.audio.setMeterVolume(0);
  store.dispatch.audio.setVolumeDetectLevel('normal');

  const { volumeInterval, volumeLevelInterval } = store.getState().audio;

  clearInterval(volumeInterval);
  clearInterval(volumeLevelInterval);

  store.dispatch.audio.setVolumeInterval(undefined);
  store.dispatch.audio.setVolumeLevelInterval(undefined);

  store.dispatch.audio.isDetectingVolume(false);

  store.dispatch.audio.setAnalyser(undefined);
}

export async function setSelectedAudioSource(payload?: MediaDeviceInfo) {
  logger.debug('set selected audio source');

  store.dispatch.scribeRecordModel._stopExistingMediaRecorder();

  store.dispatch.audio._setSelectedAudioSource(payload);

  setLastUsedAudioInput(payload?.deviceId);

  // const { isRunning } = store.getState().timerModel;

  const { isDetectingVolume } = store.getState().audio;
  if (isDetectingVolume) {
    store.dispatch.audio.stopVolumeDetection();
    await sleep(500);
    void stop();
    await init();
    await store.dispatch.audio.startVolumeDetection();
    // never restarted
  }

  // if (isRunning) {
  //   if (!USE_CHUNK_RECORDING()) {
  //     void dispatch.scribeRecordModel.handleSocketOpen();
  //   }
  //   void dispatch.scribeRecordModel.startBackupOfflineRecording();
  // }
}

export async function initializeAudioDevices() {
  try {
    // dispatch.audio.setPermissionDenied(false);

    if (!store.getState().audio.mediaStreamAudioSourceNode) {
      await init();
    }

    const audioDevices = (await navigator.mediaDevices.enumerateDevices()).filter(
      (device) => device.kind === 'audioinput'
    );

    const defaultDeviceLabel = audioDevices.find(
      (device) => device.deviceId === 'default' || device.deviceId === 'communications'
    )?.label;

    const defaultDevice = audioDevices.find(
      (d) =>
        d.deviceId !== 'default' &&
        d.deviceId !== 'communications' &&
        defaultDeviceLabel?.includes(d.label)
    );

    const filteredDevices = audioDevices.filter(
      (d) => d.deviceId !== 'default' && d.deviceId !== 'communications'
    );

    const filteredDeviceIds = filteredDevices.map((d) => d.deviceId);
    const currentDeviceIds = store
      .getState()
      .audio.audioSourceDevices.map((d) => d.deviceId);

    // the list of devices hasn't changed
    if (isEqual(filteredDeviceIds, currentDeviceIds)) {
      logger.debug('same devices');
      return;
    }

    logger.debug('audioDevices', audioDevices);
    store.dispatch.audio.clearSelectedAudioSource();

    if (audioDevices.length > 0) {
      let previous: MediaDeviceInfo | null = null;
      if (
        getDefaultStore().get(lastUsedAudioInputAtom) ||
        store.getState().audio.selectedAudioSource
      ) {
        const last =
          getDefaultStore().get(lastUsedAudioInputAtom) ||
          store.getState().audio.selectedAudioSource?.deviceId;
        logger.debug('last', last);
        previous = audioDevices.find((x) => x.deviceId === (last || '')) || null;
        if (!previous) {
          setLastUsedAudioInput(undefined);
        }
      }

      logger.debug('previous', previous);
      logger.debug('defaultDevice', defaultDevice);
      const selectedDevice = previous || defaultDevice || audioDevices.at(0);
      logger.debug('selectedDevice', selectedDevice);
      await store.dispatch.audio.setSelectedAudioSource(selectedDevice);
    }

    logger.debug('filteredAudioDevices', filteredDevices);
    store.dispatch.audio.setAudioSourceDevices(filteredDevices);
    logger.debug(store.getState().audio.audioSourceDevices);
  } catch (e) {
    logger.error('Error getting audio devices', e);
    if (e instanceof Error && e?.name === 'NotAllowedError') {
      logger.debug('Permission Denied');
      // dispatch.chunkAudio.setPermissionDenied(true);
    }
  }
}
